<script setup>
/**
 * @description 全局通用的气泡卡片组件，基于 Arco Design Trigger 封装
 * @reason 统一气泡卡片样式：默认不显示箭头，内容内边距固定为 24px，圆角 12px
 * @param {string} [position='bottom'] - 气泡出现的位置
 * @param {string} [trigger='hover'] - 触发方式
 * @param {boolean} [showArrow=false] - 是否显示箭头
 * @param {boolean|null} [popupVisible=null] - 受控显示状态，null 时交由触发器控制
 * @param {string} [contentClass=''] - 内容浮层自定义 class
 * @param {Object} [contentStyle={}] - 内容区域自定义样式
 * @slot default - 触发气泡的元素
 * @slot content - 气泡内容
 */

// 1. Imports
import { computed } from 'vue'
import { Trigger } from '@arco-design/web-vue'

// 2. Props
const props = defineProps({
  /**
   * @description 气泡出现的位置
   * @type {'top'|'tl'|'tr'|'bottom'|'bl'|'br'|'left'|'lt'|'lb'|'right'|'rt'|'rb'}
   */
  position: {
    type: String,
    default: 'bottom',
  },
  /**
   * @description 触发方式
   * @type {'hover'|'click'|'focus'|'contextMenu'}
   */
  trigger: {
    type: String,
    default: 'hover',
  },
  /**
   * @description 是否显示箭头
   * @type {boolean}
   */
  showArrow: {
    type: Boolean,
    default: false,
  },
  popupVisible: {
    type: Boolean,
    default: null,
  },
  contentClass: {
    type: [String, Array, Object],
    default: '',
  },
  /**
   * @description 内容区域自定义样式
   * @type {Object}
   */
  contentStyle: {
    type: Object,
    default: () => ({}),
  },
  popupContainer: {
    type: [String, Object],
    default: undefined,
  },
  popupStyle: {
    type: Object,
    default: () => ({}),
  },
  overlayStyle: {
    type: Object,
    default: () => ({}),
  },
  autoFitPosition: {
    type: Boolean,
    default: true,
  },
  scrollToClose: {
    type: Boolean,
    default: false,
  },
  scrollToCloseDistance: {
    type: Number,
    default: 0,
  },
})

const emit = defineEmits(['popup-visible-change'])

const handlePopupVisibleChange = (visible) => {
  emit('popup-visible-change', visible)
}

const mergedContentClass = computed(() => ['arco-popover-popup-content', props.contentClass])
const mergedContentStyle = computed(() => ({
  padding: '24px',
  borderRadius: '12px',
  ...props.contentStyle,
}))
const mergedPopupStyle = computed(() => ({
  ...props.overlayStyle,
  ...props.popupStyle,
}))
</script>

<template>
  <Trigger
    v-bind="$attrs"
    class="base-popover arco-popover"
    :position="position"
    :trigger="trigger"
    :show-arrow="showArrow"
    :popup-visible="popupVisible === null ? undefined : popupVisible"
    :content-class="mergedContentClass"
    :content-style="mergedContentStyle"
    :popup-container="popupContainer"
    :popup-style="mergedPopupStyle"
    :popup-offset="10"
    :auto-fit-position="autoFitPosition"
    :scroll-to-close="scrollToClose"
    :scroll-to-close-distance="scrollToCloseDistance"
    animation-name="zoom-in-fade-out"
    auto-fit-transform-origin
    @popup-visible-change="handlePopupVisibleChange"
  >
    <slot />
    <template #content>
      <div class="arco-popover-content">
        <slot name="content" />
      </div>
    </template>
  </Trigger>
</template>

<style lang="scss">
/**
 * @description 气泡卡片全局样式覆盖
 * @reason 确保内边距和圆角符合设计规范
 */
.base-popover {
  .arco-popover-content {
    padding: 0; // 内部由 content-style 控制
    margin-top: 0;
  }
}
</style>
